import { Component, OnInit, Input } from '@angular/core';
import { HelpService } from '../help-service/help.service';
import { Attribute } from '../attribute/Attribute';
import { AttributeType } from '../attribute/IAttribute';
import { TranslateService } from '@ngx-translate/core';
import { INode } from '../Configuration/INode';

@Component({
    selector: 'wbm-config-attribute-help',
    templateUrl: 'config-help-attribute.component.html',
    styleUrls: ['../../assets/theme/css/ConfigHelp.css']
})
export class ConfigHelpAttributeComponent implements OnInit {

    emptyLabel: string = 'empty';
    elementLabel: string = 'for-each-element';

    attributeType = AttributeType;

    @Input() public attribute: Attribute;
    @Input() public classname: string;
    @Input() public node: INode;
    @Input() public attributePath: string;

    get attributeHelp(): string {
        return this._helpService.getHelpContent(this.classname, this.attribute.name, this.attributePath);
    }

    get attributeFormat(): string {
        if (this.attribute.structure && this.attribute.structure['type'] && this.attribute.structure['type']['name']) {
            const type = this.attribute.structure['type']['name'];
            let translatedType: string = type;
            this._translate.get(type)
                .subscribe((translated) => {
                    translatedType = translated;
                });
            if (this.attribute.isOctetString) {
                const pattern = this.attribute.structure['type'].pattern;
                return this.getDisplayableConstraintFromPattern(pattern);
            } else if (this.attribute.isInteger) {
                const acceptedValues: Array<any> = this.attribute.structure['type'].values;
                return translatedType + ' ' + this.getDisplayableConstraintFromIntegerConstraints(acceptedValues);
            } else if (this.attribute.isEnumerated) {
                return translatedType;
            } else if (this.attribute.isByteString && this.attribute.structure['type']['digitValue']) {
                return this.elementLabel + ': ' + this.attribute.structure['type']['digitValue'].join(', ');
            } else if (this.attribute.isBoolean) {
                return translatedType;
            }
        }
        return undefined;
    }

    get attributeDefaultValue(): string {
        const defaultValue = this.attribute.defaultValue;
        if (defaultValue !== undefined) {
            if (this.attribute.isBoolean || this.attribute.isInteger) {
                return defaultValue.toString();
            } else if (this.attribute.isEnumerated) {
                //translate default value if type is 'enumerated'
                let translatedValue: string = defaultValue;
                this._translate.get(defaultValue).subscribe((translated) => translatedValue = translated);
                return translatedValue;
            } else if (this.attribute.isOctetString) {
                return defaultValue !== '' ? defaultValue : '{' + this.emptyLabel + '}';
            } else if (this.attribute.isByteString) {
                return defaultValue.split('').join(', ');
            }
        }
        return defaultValue;
    }

    get isAttrDisabled(): boolean {
        if (this.node.isObjectNode) {
            return !this.attribute.isUsed || (this.node.isLocal ? !this.attribute.isCreatable : !this.attribute.isSettable);
        }
        return false;
    }

    constructor(
        private _helpService: HelpService,
        private _translate: TranslateService
    ) { }

    ngOnInit() {
        this._translate.get(this.emptyLabel).subscribe((translated) => { this.emptyLabel = translated; });
        this._translate.get(this.elementLabel).subscribe((translated) => { this.elementLabel = translated; });
    }

    attributeDigitHelp(digit: string): string {
        return this._helpService.getHelpContent(this.classname, this.attribute.name, this.attributePath + '/' + digit);
    }

    private getDisplayableConstraintFromIntegerConstraints(constraints: Array<any>): string {
        let result = '';
        let constraintsLogic: Object = {
            'or': []
        };
        if (constraints) {
            constraints.forEach(constraint => {
                if (Array.isArray(constraint) && constraint.length === 2) {
                    constraintsLogic['or'].push('[' + constraint[0] + ' - ' + constraint[1] + ']');
                } else {
                    constraintsLogic['or'].push(constraint);
                }
            });
        }
        result = constraintsLogic['or'].join(', ');
        return result;
    }

    private getDisplayableConstraintFromPattern(pattern: string): string {
        let translatedCharacters = 'characters';
        this._translate.get('characters')
            .subscribe((translated) => {
                translatedCharacters = translated;
            });

        if (pattern === undefined) {
            return translatedCharacters;
        }

        //search for constraint on valid characters
        let validCharConstraint = '';
        let validChars: Array<string> = [];
        const insideBracket: string[] = pattern.split(/[\[\]]+/);
        if (insideBracket.length > 1) {
            let validExp: string = insideBracket[1];
            for (let validExpIdx = 0, c = ''; c = validExp.charAt(validExpIdx); ) {
                if (c === '\\') {
                    if (validExp.length > validExpIdx + 1) {
                        validChars.push(validExp.charAt(validExpIdx + 1));
                        validExpIdx += 1;
                    }
                } else if (c === '-') {
                    const currentValidCharIdx = validChars.length - 1;
                    validChars[currentValidCharIdx] += c;
                    if (validExp.length > validExpIdx + 1) {
                        validChars[currentValidCharIdx] += validExp.charAt(validExpIdx + 1);
                        validExpIdx += 1;
                    }
                } else if (c !== ' ') {
                    validChars.push(c);
                }
                validExpIdx++;
            }
        }
        if (validChars.length > 0) {
            validCharConstraint = validChars.join(', ');
        }

        //search for constraint on number of characters
        let nbCharConstraint = '';
        const insideCurlyBracket: string[] = pattern.split(/[{}]+/);
        if (insideCurlyBracket.length > 1) {
            let range: string = insideCurlyBracket[1];
            if (range.split(',').length > 1) {
                const nbCharMin = range.split(',')[0];
                const nbCharMax = range.split(',')[1];
                if (nbCharMin === nbCharMax) {
                    nbCharConstraint = nbCharMin;
                } else {
                    nbCharConstraint = nbCharMin + ' - ' + nbCharMax;
                }
            }
        }

        //build final constraint string
        return nbCharConstraint + ' ' + translatedCharacters + (validCharConstraint.length > 0 ? ': ' : '') + validCharConstraint;
    }
}
